import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  AppWindow,
  Boxes,
  KeyRound,
  LayoutDashboard,
  Menu,
  Settings,
  ShieldCheck,
  Users,
  X,
} from "lucide-react";
import Brand from "./Brand";
import NavGroup from "./NavGroup";
import UserMenu from "./UserMenu";
import { useApplicationsContext } from "../context/ApplicationsContext";
import { useAuth } from "../context/AuthContext";

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { applications } = useApplicationsContext();
  const currentUserCanManageUsers = Boolean(user?.can_manage_users);
  const currentUserCanManageApplications = Boolean(user?.can_manage_applications);
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Navigating on mobile should close the drawer behind you.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const applicationItems = useMemo(
    () =>
      applications.map((application) => ({
        to: `/dashboard/applications/${application.id}`,
        label: application.title,
        icon: AppWindow,
        end: true,
      })),
    [applications]
  );

  // useParams() in a layout route only sees its own segments, not a child's `:applicationId`.
  const activeApplicationId = location.pathname.match(/^\/dashboard\/applications\/([^/]+)/)?.[1];
  const activeApplication = applications.find(
    (application) => String(application.id) === activeApplicationId
  );

  // Only the sections this user is allowed into.
  const managementItems = [
    currentUserCanManageUsers && {
      to: "/dashboard/manage/users",
      label: "مدیریت کاربران",
      icon: Users,
      end: true,
    },
    currentUserCanManageApplications && {
      to: "/dashboard/manage/applications",
      label: "مدیریت اپلیکیشن‌ها",
      icon: Boxes,
      end: true,
    },
  ].filter(Boolean);

  const PAGE_TITLES = {
    "/dashboard/manage/users": "مدیریت کاربران",
    "/dashboard/manage/applications": "مدیریت اپلیکیشن‌ها",
    "/dashboard/settings/password": "تغییر رمز عبور",
  };

  const pageTitle =
    PAGE_TITLES[location.pathname] || (activeApplication ? activeApplication.title : "داشبورد");

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="shell">
      <aside className={`sidebar${menuOpen ? " open" : ""}`}>
        <div className="sidebar-head">
          <NavLink to="/dashboard">
            <Brand subtitle="پنل مدیریت" size={40} />
          </NavLink>
          <button
            type="button"
            className="icon-btn sidebar-close"
            onClick={() => setMenuOpen(false)}
            aria-label="بستن منو"
          >
            <X size={18} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>

        <nav className="nav">
          <NavLink
            to="/dashboard"
            end
            className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
          >
            <LayoutDashboard className="nav-icon" size={19} strokeWidth={1.9} aria-hidden="true" />
            <span className="nav-label">داشبورد</span>
          </NavLink>

          <NavGroup
            icon={Boxes}
            label="اپلیکیشن‌ها"
            items={applicationItems}
            defaultOpen
            emptyLabel="اپلیکیشنی ثبت نشده است"
          />

          {managementItems.length > 0 && (
            <NavGroup
              icon={ShieldCheck}
              label="مدیریت"
              items={managementItems}
              defaultOpen={location.pathname.startsWith("/dashboard/manage")}
            />
          )}

          <NavGroup
            icon={Settings}
            label="تنظیمات"
            defaultOpen={location.pathname.startsWith("/dashboard/settings")}
            items={[
              {
                to: "/dashboard/settings/password",
                label: "تغییر رمز عبور",
                icon: KeyRound,
                end: true,
              },
            ]}
          />
        </nav>

        <div className="sidebar-foot">
          <span>نسخه اولیه</span>
        </div>
      </aside>

      <div
        className={`scrim${menuOpen ? " show" : ""}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />

      <div className="shell-main">
        <header className="topbar">
          <div className="topbar-start">
            <button
              type="button"
              className="icon-btn burger"
              onClick={() => setMenuOpen(true)}
              aria-label="نمایش منو"
            >
              <Menu size={20} strokeWidth={2} aria-hidden="true" />
            </button>
            <h2>{pageTitle}</h2>
          </div>

          <UserMenu user={user} onLogout={handleLogout} />
        </header>

        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
